import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Button,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Avatar,
  Stack,
} from '@mui/material'
import { Delete, Add } from '@mui/icons-material'
import { USAGE_TYPES } from '../../constants'
import { getIcon } from '../NavigationItem'

export interface LoopInspectorProps {
  loop: any
  loopIndex: number
  onChange: (data: any) => void
  onNavigateToSegment?: (segment: any, segmentIndex: number) => void
  readonly?: boolean
}

const LoopInspector = React.memo<LoopInspectorProps>(
  ({ loop, loopIndex, onChange, onNavigateToSegment, readonly = false }) => {
    const [localLoop, setLocalLoop] = useState(loop)

    useEffect(() => {
      setLocalLoop(loop)
    }, [loop])

    const handleFieldChange = useCallback(
      (field: string, value: any) => {
        const updatedLoop = {
          ...localLoop,
          [field]: value,
        }
        setLocalLoop(updatedLoop)
        onChange(updatedLoop)
      },
      [localLoop, onChange]
    )

    const handleAddSegment = useCallback(() => {
      const newSegment = {
        id: `segment-${Date.now()}`,
        name: 'New Segment',
        usage: 'M',
        elements: [],
      }

      const updatedSegments = [...(localLoop.segments || []), newSegment]
      const updatedLoop = {
        ...localLoop,
        segments: updatedSegments,
      }
      setLocalLoop(updatedLoop)
      onChange(updatedLoop)
    }, [localLoop, onChange])

    const handleRemoveSegment = useCallback(
      (segmentIndex: number) => {
        const updatedSegments =
          localLoop.segments?.filter((_: any, index: number) => index !== segmentIndex) || []
        const updatedLoop = {
          ...localLoop,
          segments: updatedSegments,
        }
        setLocalLoop(updatedLoop)
        onChange(updatedLoop)
      },
      [localLoop, onChange]
    )

    const getUsageColor = (usage: string) => {
      switch (usage) {
        case 'M':
          return 'primary'
        case 'O':
          return 'secondary'
        case 'C':
          return 'warning'
        default:
          return 'default'
      }
    }

    const getUsageLabel = (usage: string) => {
      switch (usage) {
        case 'M':
          return 'Mandatory'
        case 'O':
          return 'Optional'
        case 'C':
          return 'Conditional'
        default:
          return usage
      }
    }

    if (!localLoop) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No loop data available
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ p: 2 }}>
        {/* Loop Properties */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight={'bold'} pb={2}>
            Loop Properties
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={localLoop.name || ''}
                onChange={e => handleFieldChange('name', e.target.value)}
                disabled={readonly}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ID"
                value={localLoop.id || ''}
                onChange={e => handleFieldChange('id', e.target.value)}
                disabled={readonly}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="loop-usage-label">Usage</InputLabel>
                <Select
                  labelId="loop-usage-label"
                  value={localLoop.usage || 'M'}
                  onChange={e => handleFieldChange('usage', e.target.value)}
                  disabled={readonly}
                  label="Usage"
                >
                  {USAGE_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Use"
                value={localLoop.max_use || ''}
                onChange={e => handleFieldChange('max_use', e.target.value)}
                disabled={readonly}
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Segments Section */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getIcon('segment')}
              <Typography variant="h6" fontWeight={'bold'}>
                Segments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({localLoop.segments?.length || 0})
              </Typography>
            </Box>
            {!readonly && (
              <Button
                startIcon={<Add />}
                onClick={handleAddSegment}
                size="small"
                variant="outlined"
              >
                Add Segment
              </Button>
            )}
          </Box>
          {localLoop.segments && localLoop.segments.length > 0 ? (
            <List dense>
              {localLoop.segments.map((segment: any, index: number) => (
                <ListItem
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: onNavigateToSegment ? 'pointer' : 'default',
                    '&:hover': onNavigateToSegment
                      ? {
                          backgroundColor: 'action.hover',
                        }
                      : {},
                  }}
                  onClick={
                    onNavigateToSegment ? () => onNavigateToSegment(segment, index) : undefined
                  }
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 2,
                        bgcolor: 'primary.main',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {segment.id ? segment.id.substring(0, 3).toUpperCase() : `SEG`}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" component="div">
                        {segment.name || `Segment ${index + 1}`}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                      <Chip
                        label={getUsageLabel(segment.usage || 'M')}
                        color={getUsageColor(segment.usage || 'M')}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`${segment.elements?.length || 0} elements`}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                  <ListItemSecondaryAction>
                    {!readonly && (
                      <IconButton
                        edge="end"
                        onClick={e => {
                          e.stopPropagation()
                          handleRemoveSegment(index)
                        }}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No segments defined
            </Typography>
          )}
        </Paper>
      </Box>
    )
  }
)

LoopInspector.displayName = 'LoopInspector'

export default LoopInspector
